import type { ChartModuleId } from '../domain/types';

const CHUNK_RECOVERY_ENTRY_KEY = 'yijing-atlas-chunk-recovery-entry';
const CHUNK_RECOVERY_MODULE_KEY = 'yijing-atlas-chunk-recovery-module';
const CHUNK_RECOVERY_NOTICE = '网站已更新，请重新点击“生成排盘”。';

const chunkErrorPatterns = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i
];

export interface ChunkRecoveryEnvironment {
  online: boolean;
  entryUrl: string;
  lastAttempt: string | null;
  rememberAttempt: (entryUrl: string) => void;
  rememberModule: (moduleId: ChartModuleId) => void;
  updateServiceWorker: () => Promise<void>;
  reload: () => void;
}

export function isChunkLoadError(cause: unknown): boolean {
  const message = cause instanceof Error ? cause.message : String(cause ?? '');
  return chunkErrorPatterns.some((pattern) => pattern.test(message));
}

function readSessionValue(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionValue(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // 浏览器禁用会话存储时仍允许刷新恢复，只是不保留所选模块。
  }
}

function currentEntryUrl(): string {
  return document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src || window.location.href;
}

function browserEnvironment(): ChunkRecoveryEnvironment {
  return {
    online: navigator.onLine,
    entryUrl: currentEntryUrl(),
    lastAttempt: readSessionValue(CHUNK_RECOVERY_ENTRY_KEY),
    rememberAttempt: (entryUrl) => writeSessionValue(CHUNK_RECOVERY_ENTRY_KEY, entryUrl),
    rememberModule: (moduleId) => writeSessionValue(CHUNK_RECOVERY_MODULE_KEY, moduleId),
    updateServiceWorker: async () => {
      const registration = await navigator.serviceWorker?.getRegistration();
      await registration?.update();
    },
    reload: () => window.location.reload()
  };
}

export async function recoverStaleChunk(
  cause: unknown,
  moduleId: ChartModuleId,
  environment?: ChunkRecoveryEnvironment
): Promise<boolean> {
  if (!isChunkLoadError(cause)) {
    return false;
  }

  const activeEnvironment = environment ?? browserEnvironment();
  if (!activeEnvironment.online || activeEnvironment.lastAttempt === activeEnvironment.entryUrl) return false;

  activeEnvironment.rememberAttempt(activeEnvironment.entryUrl);
  activeEnvironment.rememberModule(moduleId);
  try {
    await activeEnvironment.updateServiceWorker();
  } catch {
    // Service Worker 更新失败时仍可通过普通刷新取得新的入口文件。
  }
  activeEnvironment.reload();
  return true;
}

export function consumeChunkRecoveryModule(): { moduleId: string; notice: string } | null {
  const moduleId = readSessionValue(CHUNK_RECOVERY_MODULE_KEY);
  if (!moduleId) return null;
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_MODULE_KEY);
  } catch {
    // 读取成功但删除失败不影响本次恢复。
  }
  return { moduleId, notice: CHUNK_RECOVERY_NOTICE };
}
